import { useState, useRef, forwardRef } from 'react';
import cn from 'classnames';
import { RelatedProduct } from 'models/RelatedProduct';
import { rm, emptyFunction } from 'utils';
import { Button } from 'components/Button';
import { Checkbox } from 'components/Shared/Form';
import Markdown from 'components/Shared/Markdown';
import { RedDrawer } from 'components/Shared/Drawer';
import Image from 'components/Shared/Image';
import styles from './addon.scss';
import { ReactComponent } from 'models/ReactComponent';
import Portal from 'components/Shared/Portal';
import { useSelector } from 'react-redux';
import { isTablet } from 'components/Shared/responsive';
import { CardPrice } from 'components/Shared/Price/themes/BGH/cardPrice';
import { getDisplayName } from 'views/ProductPage/addons/themes/BGH/utils';
import { DISCOUNT_PERCENT_THRESHOLD } from 'views/ProductPage/addons/themes/BGH/groupAddon';

const DRAWER_IMAGE_DIMENSIONS = {
  width: 98,
  height: 98
};
const MOBILE_ADDONS_DRAWER_IMAGE_DIMENSIONS = {
  width: 414,
  height: 414
};

const DRAWER_ADDON_IMAGE_HOVER_DIMENSIONS = {
  width: 414,
  height: 414
};

interface DrawerAddonProductProps extends RelatedProduct {
  onClick: () => void;
  shortDescription: ReactComponent;
  isMobile?: boolean;
}

export const DrawerAddonProduct = forwardRef<HTMLDivElement, DrawerAddonProductProps>(
  (
    {
      onClick,
      productId,
      selected,
      productImage,
      title,
      price,
      quantity,
      isCampaign,
      isLoweredPrice,
      isLowPrice,
      shortDescription,
      isMobile
    },
    ref
  ) => {
    const [infoVisible, setInfoVisible] = useState(false);
    const tablet = useSelector(isTablet);
    const isMobileOrTablet = tablet || isMobile;

    const extendedInfoRef = useRef<HTMLDivElement>(null);

    const shouldShowDiscount = !!(price.discountPercent || 0 >= DISCOUNT_PERCENT_THRESHOLD);
    const shouldShowCampaign = isCampaign && shouldShowDiscount;
    const shouldShowLoweredPrice = isLoweredPrice && shouldShowDiscount;
    const shouldShowLowPrice = isLowPrice && shouldShowDiscount;

    const formattedDisplayName = getDisplayName({
      displayName: title,
      quantity: quantity.amount,
      unit: quantity.unit
    });

    const openInfo = () => setInfoVisible(true);

    const closeInfo = () => setInfoVisible(false);
    return (
      <>
        <div
          ref={ref}
          onMouseEnter={!isMobileOrTablet ? openInfo : emptyFunction}
          onMouseLeave={!isMobileOrTablet ? closeInfo : emptyFunction}
          onClick={!isMobileOrTablet ? onClick : emptyFunction}
          key={productId}
          className={cn(styles.addon, styles.drawerButton, {
            [styles.selected]: selected
          })}
        >
          <div className={styles.mainInfoContainer}>
            <Image {...productImage} {...DRAWER_IMAGE_DIMENSIONS} className={styles.image} />

            <div className={styles.content}>
              <div className={styles.top}>
                <div className={styles.name}>{formattedDisplayName}</div>
              </div>
              <CardPrice
                price={price}
                page="addons"
                hasCampaignStyle={shouldShowCampaign}
                isCampaign={shouldShowCampaign}
                isLowPrice={shouldShowLowPrice}
                isLoweredPrice={shouldShowLoweredPrice}
              />
              {selected && !isMobileOrTablet && (
                <Checkbox
                  appearance="tertiary"
                  className={cn(styles.checkbox, {
                    [styles.discount]:
                      shouldShowCampaign || shouldShowLowPrice || shouldShowLoweredPrice
                  })}
                  color="charcoal"
                  checked
                  tabIndex={-1}
                  readOnly
                />
              )}

              {isMobileOrTablet && (
                <>
                  <div className={styles.buyReadMoreButtons}>
                    {selected ? (
                      <Button onClick={onClick} className={styles.button} appearance="secondary">
                        {rm('product_addon_remove')}
                      </Button>
                    ) : (
                      <Button onClick={onClick} className={styles.button} appearance="quaternary">
                        {rm('product_addons_slider_header')}
                      </Button>
                    )}
                    <Button onClick={openInfo} className={styles.button} appearance="whitePill">
                      {rm('read_more')}
                    </Button>
                  </div>
                  <RedDrawer title={title} isOpen={infoVisible} onClose={closeInfo}>
                    <div className={styles.drawerImageWrapper}>
                      <Image
                        className={styles.drawerImage}
                        {...MOBILE_ADDONS_DRAWER_IMAGE_DIMENSIONS}
                        paddingBottom
                        wrapperStyle={{
                          height: '0',
                          width: '100%'
                        }}
                        {...productImage}
                      />
                    </div>
                    <Markdown {...shortDescription} />
                  </RedDrawer>
                </>
              )}
            </div>

            {!isMobileOrTablet && (
              <Portal>
                <div
                  ref={extendedInfoRef}
                  className={cn(styles.addonExtendedInfo, {
                    [styles.visible]: infoVisible
                  })}
                >
                  <Image
                    wrapperStyle={{ width: '100%' }}
                    paddingBottom
                    {...productImage}
                    {...DRAWER_ADDON_IMAGE_HOVER_DIMENSIONS}
                  />
                  <Markdown className={styles.addonDescription} {...shortDescription} />
                </div>
              </Portal>
            )}
          </div>
        </div>
      </>
    );
  }
);
